import { html, LitElement, TemplateResult } from 'lit';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { customElement, property, query } from 'lit/decorators.js';
import { PolicyPanelManager } from '../../../../../PolicyPanelManager';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { LocalSandbox } from '@nirvana/shared/dtos/types/policies/common';
import { LabelWidthValues } from '../../../../../../../../types/common/types';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { SelectOption } from '../../../../../../../../ui/gzDropdown/types';
import { styles } from '../view/styles';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { GzInfoBoxWebComponent } from '../../../../../../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { FormEvent } from '../../../../../../../../ui/gzForm/formField/types/form-event.enum';
import { getDropdownSelectionFromLocalSandboxes } from '../utils';

@customElement('gz-policy-panel-sandbox-local-instance')
export class GzLocalSandboxInstanceWebComponent extends LitElement {
  @property({ type: Function, attribute: true, reflect: true })
  public getLocalSandboxes: () => Promise<{ success: boolean; data: LocalSandbox[]; total: number }>;

  @property({ type: Boolean, attribute: true, reflect: true })
  public hasInfoText = false;

  @query('#local-sandbox-instance-form-field')
  private localSandboxInstanceFormField: GzFormFieldWebComponent<GzDropdownWebComponent, number | string>;
  @query('gz-info-box')
  private gzInfoBox: GzInfoBoxWebComponent;

  public formGroup: FormGroup;

  /**
   * Template styles.
   */
  static get styles() {
    return styles;
  }

  /**
   * Set selected option.
   * @param option
   */
  public set selectedOption(option: SelectOption) {
    this.localSandboxInstanceFormField.Input.options = [option];
  }

  /**
   * Set disabled state.
   * If disabled, reset field and hide warning box.
   * If enabled, validate field.
   * @param disabled
   */
  public set disabled(disabled: boolean) {
    this.localSandboxInstanceFormField.disabled = disabled;
    toggleVisibility(this.gzInfoBox, this.isWarningBoxVisible(disabled));

    if (disabled) {
      this.localSandboxInstanceFormField.Validators = [];
      this.localSandboxInstanceFormField.resetField();
    } else {
      this.localSandboxInstanceFormField.Validators = [RequiredValidator(translateFn('Required field'))];
      this.localSandboxInstanceFormField.validate(true);
    }
  }

  /**
   * Get value as local sandbox object from endpoint id.
   */
  public get value(): LocalSandbox | undefined {
    return PolicyPanelManager.getInstance().getLocalSandboxPolicyInfoByEndpointId(
      this.localSandboxInstanceFormField.value as string
    );
  }

  protected firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);

    this.buildFormGroup();
    this.setValidators();
  }

  protected render() {
    return html`
      <gz-form-field id="local-sandbox-instance-form-field">
        <gz-dropdown
          gz-form-input
          type="dropdown"
          .labelWidth="${LabelWidthValues.XLARGE}"
          .label="${translateFn('Select a Sandbox Analyzer instance')}"
          .noResultsFound="${translateFn('No local Sandbox Analyzer instance available.')}"
          .placeholder="${translateFn('No Sandbox Analyzer instance selected.')}"
          @open="${this.onLocalSandboxDropdownOpen}"
        ></gz-dropdown>
        <gz-input-error></gz-input-error>
        ${this.renderInfoText()}
      </gz-form-field>

      <gz-info-box
        severity="warning"
        .innerHTML=${translateFn(
          'No local Sandbox Analyzer instance available. To install Sandbox Analyzer locally, refer to {link}this article{/link}.',
          {
            link: '<gz-link iconsize="20" type="underline" color="secondary" href="https://www.bitdefender.com/business/support/en/77458-311779-deploy-sandbox-analyzer-virtual-appliance.html"><span>',
            '/link': '</span></gz-link>',
          }
        )}
      >
      </gz-info-box>
    `;
  }

  private renderInfoText(): TemplateResult | undefined {
    if (!this.hasInfoText) {
      return;
    }

    return html` <gz-text fontSize="12" fontWeight="400" class="form-field-info-text"
      >${translateFn('Specify the pre-installed Sandbox Analyzer to be used for submitting content.')}
    </gz-text>`;
  }

  private setValidators() {
    this.localSandboxInstanceFormField.addEventListener(
      FormEvent.ValueChange,
      this.setLocalSandboxValidators.bind(this),
      {
        once: true,
      }
    );
  }

  private setLocalSandboxValidators() {
    this.localSandboxInstanceFormField.Validators = [RequiredValidator(translateFn('Required field'))];
  }

  private buildFormGroup() {
    this.formGroup = FormGroup.build({
      endpointId: this.localSandboxInstanceFormField,
    });
  }

  private async onLocalSandboxDropdownOpen() {
    // get local sandbox instances from manager
    const localSandboxes = PolicyPanelManager.getInstance().getLocalSandboxesPolicyInfo();
    if (localSandboxes) {
      this.localSandboxInstanceFormField.Input.options = getDropdownSelectionFromLocalSandboxes(localSandboxes);
      return;
    }

    this.localSandboxInstanceFormField.Input.isLoading = true;
    let dataToSave: LocalSandbox[] = [];

    // get local sandbox instances from backend
    try {
      const response = await this.getLocalSandboxes();
      if (response.data) {
        dataToSave = response.data;
      }

      this.localSandboxInstanceFormField.Input.options = getDropdownSelectionFromLocalSandboxes(dataToSave);
    } catch (error) {
      console.log(error);
    } finally {
      PolicyPanelManager.getInstance().setLocalSandboxesPolicyInfo(dataToSave);
      toggleVisibility(this.gzInfoBox, this.isWarningBoxVisible(false));
      this.localSandboxInstanceFormField.Input.isLoading = false;
    }
  }

  private isWarningBoxVisible(disabled: boolean): boolean {
    if (disabled) {
      return false;
    }

    if (
      this.localSandboxInstanceFormField?.Input.options.length &&
      this.localSandboxInstanceFormField?.Input.options[0].value
    ) {
      return false;
    }

    const localSandboxes = PolicyPanelManager.getInstance().getLocalSandboxesPolicyInfo();

    // not loaded yet
    if (!localSandboxes) {
      return false;
    }

    // no local sandbox defined
    return !localSandboxes.length;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-policy-panel-sandbox-local-instance': GzLocalSandboxInstanceWebComponent;
  }
}
